// Libs
import { Box, Stack, Typography } from '@mui/material';
import { ApexOptions } from 'apexcharts';
import classNames from 'classnames/bind';
import { useEffect, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
// Others
import { IJobDistributionCategoryRes } from '~/utils/interface/report';
// Styles, images, icons
import styles from './DonutChart.module.scss';

type Props = {
  title?: string;
  data: IJobDistributionCategoryRes[];
  height?: number | string;
  colors?: string[];
};

const cx = classNames.bind(styles);

const DEFAULT_COLORS = [
  '#4CAF50', // Completed - green
  '#2196F3', // Estimate - blue
  '#FF9800', // No Go - orange
  '#9C27B0', // Recall - purple
  '#795548', // J.O.H - brown
  '#00BCD4', // J.I.P. - cyan
  '#F44336', // Canceled - red
];

const DonutChart = (props: Props) => {
  //#region Destructuring Props
  const { title, data = [], height = 400, colors = DEFAULT_COLORS } = props;
  //#endregion Destructuring Props

  //#region Declare State
  const [configChart, setConfigChart] = useState<ApexOptions | null>(null);
  //#endregion Declare State

  //#region Implement Hook
  useEffect(() => {
    if (!data || data.length === 0) {
      setConfigChart(null);
      return;
    }

    const labels = data.map((item) => item.label);

    const options: ApexOptions = {
      chart: {
        type: 'donut',
        toolbar: { show: false },
        animations: { enabled: true },
      },
      labels,
      colors: colors.slice(0, labels.length),
      legend: {
        position: 'bottom',
        horizontalAlign: 'center',
        fontSize: '13px',
        markers: { size: 8 },
        itemMargin: { horizontal: 8, vertical: 4 },
      },
      dataLabels: {
        enabled: true,
        formatter: (val: number, opts: { seriesIndex: number; w: { config: { series: number[] } } }) => {
          const count = opts.w.config.series[opts.seriesIndex];
          return count === 0 ? '' : `${count}`;
        },
        style: { fontSize: '12px', fontWeight: '500' },
        dropShadow: { enabled: false },
      },
      plotOptions: {
        pie: {
          donut: {
            size: '55%',
            labels: {
              show: true,
              total: {
                show: true,
                label: 'Total',
                fontSize: '14px',
                fontWeight: '500',
                formatter: (w: { globals: { seriesTotals: number[] } }) =>
                  `${w.globals.seriesTotals.reduce((a, b) => a + b, 0)}`,
              },
            },
          },
        },
      },
      tooltip: {
        y: {
          formatter: (val: number) => `${val} job${val !== 1 ? 's' : ''}`,
        },
      },
      stroke: { width: 2 },
      states: {
        hover: { filter: { type: 'darken' } },
      },
    };

    setConfigChart(options);
  }, [data, colors]);
  //#endregion Implement Hook

  const hasData = data.some((item) => item.count > 0);

  return (
    <Box component='div' className={cx('container')}>
      {title && (
        <Stack className={cx('header')} mb={1.5}>
          <Typography variant='h6' className={cx('title')}>
            {title}
          </Typography>
        </Stack>
      )}

      {!hasData || !configChart ? (
        <Box component='div' className={cx('empty')} height={height}>
          <Typography className={cx('emptyText')}>No data available</Typography>
        </Box>
      ) : (
        <ReactApexChart
          options={configChart}
          series={data.map((item) => item.count)}
          type='donut'
          height={height}
          width='100%'
        />
      )}
    </Box>
  );
};

export default DonutChart;
